import { withNamespaces } from 'react-i18next';
import { Button, Table } from 'reactstrap';

import {
    CyberSecurityStatuses,
    DocumentForensicsFieldStatuses,
    DocumentForensicsOutcomes,
} from '../../constants';

const FieldStatus = ({
    statusKey,
    t,
}) => {
    const status = DocumentForensicsFieldStatuses[statusKey];

    if (!status) return null;

    return (
        <span className={status.className}>
            <i className={`${status.icon} align-middle me-1`} />
            {t(status.title)}
        </span>
    );
};

const DocumentDetail = ({
    document,
    t,
}) => {
    const outcome = DocumentForensicsOutcomes[document.outcome];
    const signalLabel = document.outcome === 'needs_review'
        ? t('{{count}} potential forgery signals detected', { count: outcome.signalCount })
        : t('{{count}} forgery signals detected', { count: outcome.signalCount });

    return (
        <div className="document-forensics-detail">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                <div>
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                        <h4 className="mb-0">{t(document.title)}</h4>
                        <span className={outcome.className}>
                            <i className={`${outcome.icon} align-middle me-1`} />
                            {t(outcome.title)}
                        </span>
                    </div>
                    <p className="text-muted mb-0">
                        {`${t(document.outcome === 'rejected' ? 'Analyzed on' : 'Submitted')} ${document.submittedAt} · ${document.fileFormat} (${document.fileSize})`}
                    </p>
                </div>

                <Button color="secondary" outline type="button">
                    <i className="ri-download-2-line align-middle me-1" />
                    {t('Download original')}
                </Button>
            </div>

            <div className="document-forensics-overview mb-4">
                <div className="document-forensics-box document-forensics-preview">
                    <img
                        alt={document.title}
                        src={document.preview}
                    />
                </div>

                <div className="document-forensics-box">
                    <div className="fw-medium mb-2">{t('Forgery likelihood')}</div>
                    <div className={`document-forensics-likelihood is-${outcome.color} mb-2`}>
                        <span>{`${outcome.likelihood}%`}</span>
                        <small>{t(outcome.likelihoodLabel)}</small>
                    </div>
                    <div className="document-forensics-gauge mb-3">
                        <span
                            className={`document-forensics-gauge-marker is-${outcome.color}`}
                            style={{ left: `${outcome.likelihood}%` }}
                        />
                    </div>
                    <p className="text-muted font-size-12 mb-0">{t(outcome.note)}</p>
                    {document.outcome !== 'verified' && (
                        <p className={`font-size-12 mb-0 mt-2 ${outcome.className}`}>
                            {signalLabel}
                        </p>
                    )}
                </div>

                <div className="document-forensics-box">
                    <div className="fw-medium mb-3">{t('AI forensics result')}</div>
                    <ul className="document-forensics-checks">
                        {document.forensics.map((check) => {
                            const status = CyberSecurityStatuses[check.status];

                            return (
                                <li key={check.title}>
                                    <i className={`${status.icon} ${status.className}`} />
                                    <span>{t(check.title)}</span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>

            <div className="document-forensics-box mb-4">
                <h5 className="mb-3">{t('Document information')}</h5>
                <div className="document-forensics-info">
                    {document.information.map((item) => (
                        <div key={item.label}>
                            <span className="text-muted d-block font-size-12">{t(item.label)}</span>
                            {item.tone ? (
                                <span className={`fw-medium text-${item.tone}`}>
                                    <i className={`${item.tone === 'success' ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'} align-middle me-1`} />
                                    {t(item.value)}
                                </span>
                            ) : (
                                <span className="fw-medium">{item.value}</span>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="document-forensics-tables">
                <div className="document-forensics-box">
                    <h5 className="mb-3">{t('Extracted data (AI)')}</h5>
                    <div className="table-responsive">
                        <Table className="table-nowrap mb-0">
                            <thead>
                                <tr>
                                    <th>{t('Field')}</th>
                                    <th>{t('Value')}</th>
                                    <th>{t('Source')}</th>
                                    <th>{t('Status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {document.extracted.map((row) => (
                                    <tr key={row.field}>
                                        <td>{t(row.field)}</td>
                                        <td>{row.value}</td>
                                        <td>{t(row.source)}</td>
                                        <td>
                                            <FieldStatus statusKey={row.status} t={t} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>

                <div className="document-forensics-box">
                    <h5 className="mb-3">{t('Cross-checking')}</h5>
                    <div className="table-responsive">
                        <Table className="table-nowrap mb-0">
                            <thead>
                                <tr>
                                    <th>{t('Field')}</th>
                                    <th>{t('Source')}</th>
                                    <th>{t('Result')}</th>
                                    <th>{t('Details')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {document.crossChecks.map((row) => (
                                    <tr key={`${row.field}-${row.source}`}>
                                        <td>{t(row.field)}</td>
                                        <td>{t(row.source)}</td>
                                        <td>
                                            <FieldStatus statusKey={row.result} t={t} />
                                        </td>
                                        <td>{t(row.details)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default withNamespaces()(DocumentDetail);
